<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { HgtUndAtd, HgtUndAtdCollection } from '@/models/HgtUndAtd';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kanton_kuerzel' },
    { direction: 'asc', property: 'jahr' },
]);
const expandedRowGroups: Ref<string[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-expandedRowGroups`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new HgtUndAtdCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(async () => {
    if (collection.loading.value !== true) {
        await collection.query();
    }
});

function editRecord(r: HgtUndAtd) {
    router.push({ name: 'bg_jw_hgt_und_atd_edit', params: { id: r?.id } });
}

async function deleteRecord(r: HgtUndAtd) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_hgt_und_atd_edit', params: { id: 'new' } });
}

type Filter = {
    property: string;
    value: any;
    id: string;
};
const filters = ref({
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:filters="filters"
            v-model:table-sort="tableSort"
            v-model:expandedRowGroups="expandedRowGroups"
            :title="$t('hgt_und_atd')"
            scrollable
            scroll-height="flex"
            paginator
            filterDisplay="row"
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as HgtUndAtd)"
            @deleterecord="deleteRecord($event as HgtUndAtd)"
            @new="newRecord"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton_id"
                sortable
                :header="$t('kanton')"
                sort-field="kanton_kuerzel"
                :showFilterMenu="false"
            >
                <template #body="slotProps">{{ slotProps.data.kanton_kuerzel }}</template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr" :showFilterMenu="false">
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect fluid v-model="filterModel.value" @change="filterCallback()"></KpJahrSelect>
                </template>
            </Column>
            <Column field="heizgradtage" sortable :header="$t('hgt')"></Column>
            <Column field="gewichtung_heizgradtage" sortable :header="$t('gewichtung_hgt')"></Column>
            <Column field="akkumulierte_temperaturdifferenzen" sortable :header="$t('atd')"></Column>
            <Column field="gewichtung_akkumulierte_temperaturdifferenzen" sortable :header="$t('gewichtung_atd')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
